import {
  parseBrLocalDate,
  formatBrLocalIsoDate,
  getItemLastUpdatedTime,
} from './brPure';
import { getItemStatusCode, isBrVerifiedItem } from './brStatus';

/**
 * GetList / AdminPending / 更名列表：统一解包 items / Items / 纯数组。
 */
export function unwrapBrApiRows(data) {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.items)) {
    return data.items;
  }
  if (Array.isArray(data.Items)) {
    return data.Items;
  }
  return [];
}

/**
 * GetList / Admin 行统一 camelCase，供供应商 runtime 与管理端共用。
 * 保留原字段，并补齐常用 camelCase，避免读侧再写 a || A。
 */
export function normalizeBrApiItem(row = {}, extras = {}) {
  if (!row || typeof row !== 'object') {
    return { ...extras };
  }
  const rejectReason = String(row.rejectReason ?? row.RejectReason ?? '').trim();
  return {
    ...row,
    ...extras,
    approvalId: row.approvalId ?? row.ApprovalId ?? null,
    businessRegistrationId: row.businessRegistrationId ?? row.BusinessRegistrationId ?? null,
    statusCode: getItemStatusCode(row),
    companyName: row.companyName || row.CompanyName || '',
    registrationNo: row.registrationNo || row.RegistrationNo || '',
    issueDate: row.issueDate || row.IssueDate || null,
    expiryDate: row.expiryDate || row.ExpiryDate || null,
    submittedDate: row.submittedDate || row.SubmittedDate || null,
    createdDate: row.createdDate || row.CreatedDate || null,
    updatedDate: row.updatedDate || row.UpdatedDate || null,
    displayNameEn: row.displayNameEn || row.DisplayNameEn || '',
    displayNameZh: row.displayNameZh || row.DisplayNameZh || '',
    currentNameEn: row.currentNameEn || row.CurrentNameEn || '',
    currentNameZh: row.currentNameZh || row.CurrentNameZh || '',
    mediaFileName: row.mediaFileName || row.MediaFileName || '',
    isActive: row.isActive ?? row.IsActive,
    inactiveReason: row.inactiveReason ?? row.InactiveReason ?? null,
    rejectReason,
    requestKind: row.requestKind || row.RequestKind || extras.requestKind || null,
  };
}

/** Submit Time 原始值：SubmittedDate → CreatedDate；勿回退 UpdatedDate */
export function getBrItemSubmittedDateRaw(item) {
  return item?.submittedDate
    || item?.SubmittedDate
    || item?.createdDate
    || item?.CreatedDate
    || null;
}

export function getItemSubmittedTime(item) {
  // 与后端展台名闸门一致：供应商 Submit Time = ApprovalHeader.CreatedDate（列表别名 SubmittedDate）
  const raw = getBrItemSubmittedDateRaw(item);
  const date = raw ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
}

function compareApprovalIdDesc(a, b) {
  const idA = Number(a?.approvalId ?? a?.ApprovalId) || 0;
  const idB = Number(b?.approvalId ?? b?.ApprovalId) || 0;
  return idB - idA;
}

/**
 * 列表排序：最后更新/提交时间新→旧（updatedDate → submittedDate → createdDate；同秒比 ApprovalId）。
 */
export function sortBrListItemsByLastUpdated(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const byUpdated = getItemLastUpdatedTime(b) - getItemLastUpdatedTime(a);
    if (byUpdated !== 0) {
      return byUpdated;
    }
    return compareApprovalIdDesc(a, b);
  });
}

/** 供应商 Submit Time 新→旧；同秒比 ApprovalId 大→小（与「最新提交」弹窗口径一致） */
export function sortBrListItemsBySubmitTime(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const bySubmit = getItemSubmittedTime(b) - getItemSubmittedTime(a);
    if (bySubmit !== 0) {
      return bySubmit;
    }
    return compareApprovalIdDesc(a, b);
  });
}

export function getBrSubmissionApprovalId(item) {
  if (!item) {
    return null;
  }
  const id = item.approvalId ?? item.ApprovalId;
  return id != null ? String(id) : null;
}

export function getBrBusinessRegistrationId(item) {
  if (!item) {
    return null;
  }
  const id = item?.businessRegistrationId ?? item?.BusinessRegistrationId;
  return id != null ? Number(id) : null;
}

export function formatBrDocumentId(item) {
  const approvalId = item?.approvalId ?? item?.ApprovalId;
  const registrationId = item?.businessRegistrationId ?? item?.BusinessRegistrationId;
  const id = approvalId || registrationId;
  return id ? `ID${id}` : '';
}

export function formatBrDate(value) {
  if (!value) {
    return '';
  }
  const date = parseBrLocalDate(value);
  if (!date) {
    return String(value).slice(0, 10);
  }
  return formatBrLocalIsoDate(date);
}

/** 展示用日期时间：yyyy-MM-dd HH:mm（不含秒） */
export function formatBrDateTime(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const raw = String(value).trim().replace('T', ' ');
    if (raw.length >= 16) {
      return raw.slice(0, 16);
    }
    return raw.slice(0, 10);
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

export function resolveBrItemCompanyName(item) {
  return item?.companyName
    || item?.CompanyName
    || item?.registeredCompanyName
    || '—';
}

export function resolveBrItemRegistrationNo(item) {
  return item?.registrationNo || item?.RegistrationNo || '—';
}

export function resolveBrItemSubmittedOnText(item) {
  if (!item) {
    return '';
  }
  // 与 getItemSubmittedTime / 排序口径一致，禁止回退 UpdatedDate
  return formatBrDateTime(getBrItemSubmittedDateRaw(item));
}

/** 列表 / 预览共用有效期文案；无到期日视为永久 */
export function formatBrItemValidityText(item, t) {
  if (!item || typeof t !== 'function') {
    return '';
  }
  const start = formatBrDate(item.issueDate || item.IssueDate);
  const end = formatBrDate(item.expiryDate || item.ExpiryDate);
  if (start && end) {
    return t('lbl_EMPBE_BR_List_Validity_Range', {
      effectiveDate: start,
      expirationDate: end,
    });
  }
  if (end) {
    return end;
  }
  return t('lbl_EMPBE_BR_List_Validity_Permanent');
}

export function resolveBrMediaUrl(fileName) {
  const value = String(fileName || '').trim();
  if (!value) {
    return '';
  }
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }
  const ossOrigin = String(process.env.VUE_APP_OSS_ORIGIN || '').trim();
  if (!ossOrigin) {
    return '';
  }
  const base = ossOrigin.endsWith('/') ? ossOrigin : `${ossOrigin}/`;
  return `${base}${value.replace(/^\/+/, '')}`;
}

export function normalizeNameForCompare(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

export function hasLinkedBusinessRegistration(item) {
  const id = getBrBusinessRegistrationId(item);
  return Number.isFinite(id) && id > 0;
}

export function filterLinkedBrListItems(items) {
  return (Array.isArray(items) ? items : []).filter(hasLinkedBusinessRegistration);
}

/**
 * 卡上 DisplayName 是否匹配当前网上展台名（中或英任一相等即可）。
 */
export function brDisplayNamesMatchCompany(item, companyNameEn, companyNameLocal) {
  const itemEn = normalizeNameForCompare(item?.displayNameEn || item?.DisplayNameEn);
  const itemZh = normalizeNameForCompare(item?.displayNameZh || item?.DisplayNameZh);
  const en = normalizeNameForCompare(companyNameEn);
  const zh = normalizeNameForCompare(companyNameLocal);
  if (en && itemEn && en === itemEn) {
    return true;
  }
  if (zh && itemZh && zh === itemZh) {
    return true;
  }
  return false;
}

/**
 * 当前展示卡：已生效中 Submit Time 最新的唯一一张。
 */
export function pickEffectiveBrItem(items) {
  const list = Array.isArray(items) ? items : [];
  const inUse = sortBrListItemsBySubmitTime(
    list.filter((item) => isBrVerifiedItem(item)),
  );
  return inUse[0] || null;
}

/**
 * @param {object} [options]
 * @param {string} [options.effectiveSectionLabelKey]
 * @param {string} [options.othersSectionLabelKey]
 */
export function groupBrListItems(items, t, options = {}) {
  const list = filterLinkedBrListItems(items);
  const effectiveItem = pickEffectiveBrItem(list);
  const effectiveId = getBrSubmissionApprovalId(effectiveItem);
  const effective = effectiveItem ? [effectiveItem] : [];
  const others = sortBrListItemsByLastUpdated(
    list.filter((item) => getBrSubmissionApprovalId(item) !== effectiveId),
  );
  const effectiveLabelKey = options.effectiveSectionLabelKey
    || 'lbl_EMPBE_BR_List_Section_Effective';
  const othersLabelKey = options.othersSectionLabelKey
    || 'lbl_EMPBE_BR_List_Section_Others';
  const sections = [];
  if (effective.length) {
    sections.push({
      key: 'effective',
      label: t(effectiveLabelKey),
      items: effective,
    });
  }
  if (others.length) {
    sections.push({
      key: 'others',
      label: t(othersLabelKey),
      items: others,
    });
  }
  return sections;
}

/** 从 groupBrListItems / buildBusinessDocumentsListSections 结果拆出两区 items */
export function pickBrDisplaySections(sections) {
  const list = Array.isArray(sections) ? sections : [];
  return {
    currentDisplayItems: list.find((row) => row?.key === 'effective')?.items || [],
    recordItems: list.find((row) => row?.key === 'others')?.items || [],
  };
}

